import { useState } from "react"
import { Link, useNavigate } from "react-router-dom"
import { motion } from "framer-motion"
import { useForm } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import { z } from "zod"
import { supabaseClient } from "@/lib/supabase/client"
import { useToast } from "@/components/ui/ToastProvider"
import { IconLoader } from "@/components/ui/Icons"

const schema = z.object({
  email: z.string().email("Valid email required"),
  username: z.string().min(3, "At least 3 characters").max(20).regex(/^[a-z0-9_]+$/, "Lowercase letters, numbers, underscores only"),
  display_name: z.string().min(2, "At least 2 characters").max(50),
  password: z.string().min(8, "At least 8 characters"),
})
type FormData = z.infer<typeof schema>

export default function RegisterPage() {
  const navigate = useNavigate()
  const { success, error: showError } = useToast()
  const [loading, setLoading] = useState(false)
  const { register, handleSubmit, formState: { errors } } = useForm<FormData>({ resolver: zodResolver(schema) })

  const onSubmit = async (data: FormData) => {
    setLoading(true)
    try {
      const { error } = await supabaseClient.auth.signUp({
        email: data.email, password: data.password,
        options: { data: { username: data.username, display_name: data.display_name } }
      })
      if (error) { showError("Sign up failed", error.message); return }
      success("Account created!", "Check your email to verify your account.")
      navigate("/auth/login")
    } finally { setLoading(false) }
  }

  return (
    <div className="min-h-screen bg-background flex flex-col items-center justify-center px-4 py-8">
      <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-[350px] space-y-3">
        <div className="text-center mb-6">
          <div className="w-14 h-14 rounded-2xl mx-auto mb-3 flex items-center justify-center" style={{ background: "var(--ig-grad)" }}>
            <svg viewBox="0 0 48 48" fill="none" className="w-8 h-8">
              <path d="M15 17.5C15 14.462 17.462 12 20.5 12H29C30.105 12 31 12.895 31 14C31 15.105 30.105 16 29 16H20.5C19.672 16 19 16.672 19 17.5C19 18.328 19.672 19 20.5 19H27.5C30.538 19 33 21.462 33 24.5C33 27.538 30.538 30 27.5 30H19C17.895 30 17 30.895 17 32C17 33.105 17.895 34 19 34H28.5C29.328 34 30 34.672 30 35.5C30 36.328 29.328 37 28.5 37H19C16.239 37 14 34.761 14 32C14 29.239 16.239 27 19 27H27.5C28.328 27 29 26.328 29 25.5C29 24.672 28.328 24 27.5 24H20.5C17.462 24 15 21.538 15 18.5V17.5Z" fill="white" />
            </svg>
          </div>
          <p className="text-text-muted text-sm">Sign up to see ideas from people you follow.</p>
        </div>
        <div className="auth-card">
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-2.5">
            {[
              { name: "email" as const, placeholder: "Email", type: "email" },
              { name: "username" as const, placeholder: "Username", type: "text" },
              { name: "display_name" as const, placeholder: "Full name", type: "text" },
              { name: "password" as const, placeholder: "Password", type: "password" },
            ].map(({ name, placeholder, type }) => (
              <div key={name}>
                <input {...register(name)} type={type} placeholder={placeholder} className="input-ig" />
                {errors[name] && <p className="text-destructive text-xs mt-1">{errors[name]?.message}</p>}
              </div>
            ))}
            <button type="submit" disabled={loading} className="btn-ig w-full py-2.5 mt-1">
              {loading ? <IconLoader className="w-4 h-4 animate-spin" /> : "Sign up"}
            </button>
          </form>
          <p className="text-center text-text-muted text-xs mt-3">
            By signing up, you agree to our Terms and Privacy Policy.
          </p>
        </div>
        <div className="auth-card text-center py-4">
          <span className="text-sm text-text-muted">Have an account? </span>
          <Link to="/auth/login" className="text-sm font-semibold text-brand">Log in</Link>
        </div>
      </motion.div>
    </div>
  )
}